/* eslint-disable no-restricted-syntax --
 * Colour MATH that computes storefront brand-token hex values from a tenant's
 * chosen primary/accent — the same kind of token-deriving logic as palette.ts,
 * not hardcoded component styling. The "use a token" rule doesn't apply here.
 */
import type { StoreFontKey } from '@erp/shared';

/* ---- minimal hex/HSL helpers (mirrors apps/web/src/lib/palette.ts) ---- */
function hex2rgb(h: string): [number, number, number] {
  let s = h.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}
function rgb2hex(r: number, g: number, b: number): string {
  const c = (v: number): string => ('0' + Math.round(Math.max(0, Math.min(255, v))).toString(16)).slice(-2);
  return '#' + c(r) + c(g) + c(b);
}
function rgb2hsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (mx + mn) / 2;
  if (mx !== mn) {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    switch (mx) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}
function hsl2hex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360; s = Math.max(0, Math.min(100, s)) / 100; l = Math.max(0, Math.min(100, l)) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return rgb2hex(f(0) * 255, f(8) * 255, f(4) * 255);
}
function hslOf(hex: string): { h: number; s: number; l: number } {
  const [h, s, l] = rgb2hsl(...hex2rgb(hex));
  return { h, s, l };
}
function adj(hex: string, dl: number): string {
  const c = hslOf(hex);
  return hsl2hex(c.h, c.s, Math.max(0, Math.min(100, c.l + dl)));
}
function isDull(hex: string): boolean {
  const c = hslOf(hex);
  return c.s < 14 || c.l < 10 || c.l > 92;
}
function deriveAccent(primary: string, accent: string | null | undefined): string {
  if (accent && !isDull(accent)) return accent;
  const c = hslOf(primary);
  return hsl2hex(c.h + 28, Math.min(85, c.s + 6), Math.min(60, Math.max(46, c.l + 8)));
}
/** Readable text colour on the brand fill — near-white or near-black by luminance. */
function onPrimary(hex: string): string {
  const [r, g, b] = hex2rgb(hex);
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.55 ? '#0b0b0b' : '#ffffff';
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Derive the full storefront brand token set from a tenant's primary (+ optional
 * accent). Returns a CSS-variable map to spread into a scoped inline `style`.
 * Invalid/absent primary ⇒ empty map (storefront falls back to app defaults).
 * Only computed hex values are emitted — never raw user input — so this is safe
 * to inject into `style`.
 */
export function deriveStoreTheme(primary: string | null | undefined, secondary?: string | null): Record<string, string> {
  if (!primary || !HEX.test(primary)) return {};
  const accent = deriveAccent(primary, secondary && HEX.test(secondary) ? secondary : null);
  return {
    // Concrete brand values for this subtree.
    '--color-brand-primary': primary,
    '--color-brand-primary-hover': adj(primary, -8),
    '--color-brand-primary-active': adj(primary, -18),
    '--color-brand-secondary': accent,
    '--color-brand-secondary-hover': adj(accent, -10),
    '--color-on-primary': onPrimary(primary),
    '--color-on-secondary': onPrimary(accent),
    // The Tailwind/shell bridge tokens (e.g. --color-brand → var(--color-brand-primary))
    // are computed on :root and inherited as-is, so overriding only --color-brand-*
    // above wouldn't reach `bg-brand`/`text-brand-contrast`. Re-alias them HERE so
    // they re-substitute from this subtree's overridden --color-brand-*.
    '--color-brand': 'var(--color-brand-primary)',
    '--color-brand-contrast': 'var(--color-on-primary)',
    '--color-fg-inverse': 'var(--color-on-primary)',
    '--primary': 'var(--color-brand-primary)',
    '--primary-600': 'var(--color-brand-primary-hover)',
    '--primary-700': 'var(--color-brand-primary-active)',
    '--on-primary': 'var(--color-on-primary)',
    '--text-onfill': 'var(--color-on-primary)',
    '--accent': 'var(--color-brand-secondary)',
    '--accent-strong': 'var(--color-brand-secondary-hover)',
    '--ring': 'var(--color-brand-primary)',
  };
}

/** Bundled font families (see apps/web/src/styles/fonts.ts). Cairo covers Arabic. */
export const STORE_FONT_FAMILIES: Record<StoreFontKey, string> = {
  geist: 'Geist, ui-sans-serif, system-ui, sans-serif',
  inter: '"Inter", ui-sans-serif, system-ui, sans-serif',
  poppins: '"Poppins", ui-sans-serif, system-ui, sans-serif',
  cairo: '"Cairo", "IBM Plex Sans Arabic", ui-sans-serif, system-ui, sans-serif',
};

/**
 * CSS-var map for a chosen font key (overrides --font-sans on the storefront
 * subtree). Cairo + IBM Plex Sans Arabic are always appended to the stack: the
 * Latin store fonts (Geist/Inter/Poppins) carry no Arabic glyphs, so the
 * browser falls through per-glyph — Latin text keeps the tenant's chosen font
 * while Arabic always renders in Cairo, matching the admin app regardless of
 * the pick (and when none is set).
 */
export function fontVar(key: StoreFontKey | null | undefined): Record<string, string> {
  const base = (key && STORE_FONT_FAMILIES[key]) || STORE_FONT_FAMILIES.geist;
  // Cairo must sit right after the Latin font — BEFORE the generic ui-sans-serif/
  // system-ui fallbacks, which themselves cover Arabic and would otherwise win
  // the per-glyph fallback. So we keep only the primary family, then Cairo.
  const primary = base.split(',')[0]?.trim() ?? 'Geist';
  return { '--font-sans': `${primary}, "Cairo", "IBM Plex Sans Arabic", ui-sans-serif, system-ui, sans-serif` };
}
